import { useI18n } from "../../../shared/i18n/i18n";
import { useMutation } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { setSessionToken } from "../../../shared/session/auth-token";
import { useTRPC } from "../../../shared/api/trpc";
import { runtimeConfig } from "../../../shared/config/runtime-config";

type UseSignInOptions = {
  invitationToken?: string;
  onSignedIn: () => void;
};

export function useSignIn({ invitationToken, onSignedIn }: UseSignInOptions) {
  const trpc = useTRPC();
  // Sent so a first-time workspace is created with names in the language
  // this browser is already showing.
  const { locale } = useI18n();
  const requestCodeMutation = useMutation(trpc.auth.requestCode.mutationOptions());
  const verifyCodeMutation = useMutation(trpc.auth.verifyCode.mutationOptions());
  const [email, setEmail] = useState(import.meta.env.DEV ? runtimeConfig.defaultDevEmail : "");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitEmail(event: FormEvent) {
    event.preventDefault();
    setError(null);

    try {
      const result = await requestCodeMutation.mutateAsync({ email, invitationToken, locale });
      if (result.sessionToken) {
        setSessionToken(result.sessionToken);
        onSignedIn();
        return;
      }
      setNeedsCode(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start sign in.");
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    setError(null);

    try {
      const result = await verifyCodeMutation.mutateAsync({ email, code, invitationToken, locale });
      setSessionToken(result.sessionToken);
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not verify code.");
    }
  }

  return {
    code,
    email,
    error,
    isSubmitting: requestCodeMutation.isPending || verifyCodeMutation.isPending,
    needsCode,
    setCode,
    setEmail,
    submit: needsCode ? submitCode : submitEmail,
  };
}
